import React, { useEffect, useState } from "react";
import "./App.css";
import "./utils/localization/i18n";
import Layout from "./shared/layout/Layout";
import { Navigate, Route, Routes } from "react-router-dom";
import { publicRoutes } from "./utils/routes/roles/publicRoutes";
import { adminRoutes } from "./utils/routes/roles/adminRoutes";
import { userRoutes } from "./utils/routes/roles/userRoutes";
import { policeRoutes } from "./utils/routes/roles/policeRoutes";
import { operatorRoutes } from "./utils/routes/roles/operatorRoutes";
import { ILayout } from "./shared/interfaces/shared/ILayout";
import { structureRoutes } from "./shared/functions/structureRoutes";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "./store/store";
import authSlice from "./store/slices/auth.slice";
import { SnackbarProvider } from "notistack";
import { AuthService } from "./auth/login/service";
import { handleError } from "./shared/functions/handleError";
import BugCatch, { bugCatchEnabled } from "./bugcatch";
import { ERole } from "./shared/enums/user/ERole";
import { IUser } from "./shared/interfaces/user/IUser";
import { getHomePath, hasRole } from "./shared/functions/userRoles";

const getRoutesForRoles = (userData?: IUser) => {
  if (hasRole(userData, ERole.ADMIN) || hasRole(userData, ERole.COMPANY)) {
    return adminRoutes;
  }
  if (hasRole(userData, ERole.POLICE)) {
    return policeRoutes;
  }
  if (hasRole(userData, ERole.OPERATOR)) {
    return operatorRoutes;
  }
  return userRoutes;
};

function App() {
  const { isAuthenticated, userData } = useSelector(
    (state: RootState) => state.auth,
  );
  const token = localStorage.getItem("token");
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    if (token) {
      setIsLoading(true);
      AuthService.me()
        .then((response) => {
          bugCatchEnabled &&
            BugCatch.setUser({
              id: String(response.id),
              email: response.email,
              username: response.username,
            });
          dispatch(
            authSlice.actions.setLoginState({
              isAuthenticated: true,
              token: token,
              userData: response,
            }),
          );
        })
        .catch(handleError)
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [token]);

  if (isLoading && !!token) {
    return (
      <div className="spinner-border" role="status">
        <span className="sr-only">Loading...</span>
      </div>
    );
  }
  return (
    <>
      <SnackbarProvider
        maxSnack={3}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
      >
        <div className="page">
          {isAuthenticated ? (
            (() => {
              const activeRoutes = getRoutesForRoles(userData);
              return (
                <Layout routes={activeRoutes as unknown as ILayout[]}>
                  <Routes>
                    {structureRoutes(activeRoutes as unknown as ILayout[])?.map(
                      (route) => (
                        <Route
                          path={route?.path}
                          element={route?.component}
                          key={route?.path}
                        />
                      ),
                    )}
                    <Route
                      path="*"
                      element={<Navigate to={getHomePath(userData)} />}
                    />
                  </Routes>
                </Layout>
              );
            })()
          ) : (
            <Routes>
              {publicRoutes?.map((route) => {
                return (
                  <Route
                    path={route?.path}
                    element={route?.component}
                    key={route?.path}
                  />
                );
              })}
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          )}
        </div>
      </SnackbarProvider>
    </>
  );
}

export default App;
